import { useRef, useState } from 'react';

import { Modal } from '@/components/Modal';
import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { CsDefectBadge } from '@/components/StatusBadge';
import { ApiRequestError } from '@/lib/api/client';
import { shortDate, shortDateTime } from '@/lib/format';
import { useCsDefect, useCsDefectActions, useCsDefects } from '@/lib/queries';
import type { CsDefect, CsDefectEvent } from '@/lib/types';
import { useSite } from '@/lib/useSite';

/**
 * 입주민 하자 (DOC-128).
 *
 * <p>입주민 앱에서 접수된 하자 가운데 <b>이 업체에 배정된 것</b>. 하도급 줄의 「맡는 하자 종류」 가
 * 맞고 하자담보기간이 남아 있으면 접수 순간 자동으로 오고, 맞는 업체가 여럿이면 본사가 골라 보낸다.
 *
 * <p>업체가 하는 일은 방문일 → 착수 → 처리 완료 · 사진이다. 방문일을 정하면 입주민에게 알림이 가고,
 * 처리 완료를 누르면 입주민이 확인해야 닫힌다 — 「아직 안 됐어요」 를 누르면 처리 중으로 돌아온다.
 * 반려는 본사만 한다.
 */
export function DefectPage() {
  const { siteId, siteName } = useSite();
  const defects = useCsDefects(siteId);
  const [openId, setOpenId] = useState<string | null>(null);

  const rows = defects.data ?? [];

  return (
    <>
      <PageHeader title="입주민 하자" subtitle={siteName} />

      <QueryState
        pending={defects.isPending}
        error={defects.isError}
        empty={rows.length === 0}
        emptyText="배정된 하자가 없습니다."
      >
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.id}>
              <button type="button" className="card w-full text-left" onClick={() => setOpenId(row.id)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-ui-md font-medium">#{row.seq} {row.title}</div>
                    <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                      {unitLabel(row)} · {row.category}
                      {row.location ? ` · ${row.location}` : ''} · {shortDate(row.createdAt)} 접수
                      {row.visitAt !== null && ` · ${shortDateTime(row.visitAt)} 방문`}
                    </div>
                  </div>
                  <CsDefectBadge status={row.status} />
                </div>
              </button>
            </li>
          ))}
        </ul>
      </QueryState>

      {openId !== null && <DefectModal id={openId} siteId={siteId} onClose={() => setOpenId(null)} />}
    </>
  );
}

function DefectModal({ id, siteId, onClose }: { id: string; siteId: string; onClose: () => void }) {
  const { canSubmitEvidence } = useSite();
  const detail = useCsDefect(id);
  const { schedule, start, complete, upload } = useCsDefectActions(siteId);
  const picker = useRef<HTMLInputElement>(null);

  const [visitAt, setVisitAt] = useState('');
  const [note, setNote] = useState('');

  const data = detail.data;
  const row = data?.row;
  const busy = schedule.isPending || start.isPending || complete.isPending || upload.isPending;

  /*
    서버가 거절한 이유. 지금 상태에서 못 하는 일은 버튼을 감추지 않고 눌렀을 때 서버가 답한다
    (「지금 상태에서는 할 수 없습니다」). 5xx 는 감춘다(MyPage 와 같은 선).
  */
  const failure = [schedule.error, start.error, complete.error, upload.error]
    .find((error): error is Error => error != null);
  const failureText = failure === undefined
    ? null
    : failure instanceof ApiRequestError && failure.status < 500
      ? failure.message
      : '지금은 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';

  /** 끝난 줄(확인 대기·완료·반려·취소)에는 손을 세우지 않는다. */
  const open = row !== undefined && ['ASSIGNED', 'SCHEDULED', 'IN_PROGRESS'].includes(row.status);
  const canAct = canSubmitEvidence && open;

  const beforePhotos = (data?.photos ?? []).filter((photo) => photo.kind === 'BEFORE');
  const afterPhotos = (data?.photos ?? []).filter((photo) => photo.kind === 'AFTER');

  return (
    <Modal
      title={row ? `#${row.seq} ${row.title}` : '입주민 하자'}
      maxWidth="36rem"
      onClose={onClose}
      actions={
        <>
          {canAct && (
            <>
              <button
                type="button"
                className="btn-secondary px-3 py-1.5 text-ui-xs"
                disabled={busy || visitAt === ''}
                onClick={() => schedule.mutate({ id, visitAt: new Date(visitAt).toISOString(), note: note.trim() || null })}
              >
                방문일
              </button>
              <button
                type="button"
                className="btn-secondary px-3 py-1.5 text-ui-xs"
                disabled={busy}
                onClick={() => start.mutate(id)}
              >
                착수
              </button>
              <button
                type="button"
                className="btn-primary px-3 py-1.5 text-ui-xs"
                disabled={busy}
                onClick={() => complete.mutate({ id, note: note.trim() || null })}
              >
                처리 완료
              </button>
            </>
          )}
          <button type="button" className="btn-secondary px-3 py-1.5 text-ui-xs" onClick={onClose}>
            닫기
          </button>
        </>
      }
    >
      <QueryState pending={detail.isPending} error={detail.isError} empty={false} emptyText="">
        {row && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <CsDefectBadge status={row.status} />
              <span className="text-ui-xs" style={{ color: 'var(--text-muted)' }}>
                {unitLabel(row)} · {row.category}
                {row.location ? ` · ${row.location}` : ''}
                {row.visitAt !== null && ` · ${shortDateTime(row.visitAt)} 방문`}
              </span>
            </div>

            {data?.body && <p className="whitespace-pre-wrap text-ui-sm">{data.body}</p>}

            <Photos label="접수 사진" photos={beforePhotos} />

            {failureText !== null && (
              <p className="text-ui-xs" style={{ color: 'var(--danger, #dc2626)' }}>{failureText}</p>
            )}

            {canAct && (
              <div className="flex flex-col gap-2">
                <label className="block">
                  <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>방문 일시</span>
                  <input
                    type="datetime-local"
                    className="input-field"
                    value={visitAt}
                    onChange={(event) => setVisitAt(event.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>입주민에게 보일 말</span>
                  <input
                    type="text"
                    className="input-field"
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                  />
                </label>
              </div>
            )}

            <div>
              <Photos label="처리 뒤 사진" photos={afterPhotos} />
              {canAct && (
                <>
                  <input
                    ref={picker}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) upload.mutate({ id, file });
                      // 같은 파일을 다시 고를 수 있어야 한다 — 값을 비우지 않으면 change 가 안 뜬다.
                      event.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    className="btn-secondary mt-2 w-full"
                    disabled={busy}
                    onClick={() => picker.current?.click()}
                  >
                    {upload.isPending ? '올리는 중입니다…' : '사진 올리기'}
                  </button>
                </>
              )}
            </div>

            <div className="rounded-md p-3" style={{ background: 'var(--bg-hover)' }}>
              <div className="mb-1 text-ui-2xs" style={{ color: 'var(--text-muted)' }}>진행</div>
              {(data?.events ?? []).map((event, index) => (
                <div key={`${event.at}-${index}`} className="text-ui-xs">
                  {EVENT_LABELS[event.type] ?? event.type}
                  {actorOf(event) && <span style={{ color: 'var(--text-muted)' }}> · {actorOf(event)}</span>}
                  <span style={{ color: 'var(--text-muted)' }}> · {shortDateTime(event.at)}</span>
                  {event.note && <p style={{ color: 'var(--text-muted)' }}>{event.note}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </QueryState>
    </Modal>
  );
}

function Photos({ label, photos }: { label: string; photos: Array<{ id: string; url: string | null }> }) {
  if (photos.length === 0) return null;
  return (
    <div>
      <div className="mb-1 text-ui-2xs" style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div className="flex flex-wrap gap-2">
        {photos.map((photo) =>
          photo.url ? (
            <a key={photo.id} href={photo.url} target="_blank" rel="noreferrer">
              <img src={photo.url} alt="" className="h-24 w-24 rounded-md object-cover" />
            </a>
          ) : null,
        )}
      </div>
    </div>
  );
}

/** 진행 줄의 「누가」. 입주민 이름은 업체 화면에 싣지 않는다. */
function actorOf(event: CsDefectEvent): string | null {
  if (event.actorKind === 'RESIDENT') return '입주민';
  return event.actorName;
}

function unitLabel(row: Pick<CsDefect, 'dong' | 'ho'>): string {
  if (!row.dong && !row.ho) return '세대 미지정';
  return `${row.dong ?? ''}동 ${row.ho ?? ''}호`.trim();
}

const EVENT_LABELS: Record<string, string> = {
  CREATED: '접수',
  ASSIGNED: '배정',
  SCHEDULED: '방문 예정',
  IN_PROGRESS: '처리 중',
  DONE: '처리 완료',
  CLOSED: '완료',
  REJECTED: '반려',
  CANCELED: '취소',
  REOPENED: '재요청',
};
